import { useEffect, useState } from "react";
import { Button, Card, DatePicker, Form, Input, Popconfirm, Select, Table, Tag } from "antd";
import dayjs from "dayjs";
import { PageHeader } from "../../components/PageHeader";
import { useApi } from "../../helpers/useApi";
import { hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { DT_FORMATS, STATUS_COLORS } from "../../helpers/constants";
import type { WorkCalendarDay } from "../../api-models";

const CLASSIFICATIONS = [
  { value: "WORKING", label: "Working day" },
  { value: "FRIDAY_HOLIDAY", label: "Friday holiday" },
  { value: "PUBLIC_HOLIDAY", label: "Public holiday" },
  { value: "EXCEPTION", label: "Exception working day" },
];

// Fridays default to public-holiday status without a stored row (spec §28.3) — this page
// only ever shows/creates/removes the OVERRIDES; the attendance engine's default still applies to
// every date with no row here.
export default function WorkCalendarPage() {
  const { user } = useUser();
  const canWrite = hasPermission(user, "WorkCalendar.Write");
  const { request, loading } = useApi();
  const [days, setDays] = useState<WorkCalendarDay[]>([]);
  const [form] = Form.useForm();

  async function load() {
    const from = dayjs().subtract(30, "day").format("YYYY-MM-DD");
    const to = dayjs().add(180, "day").format("YYYY-MM-DD");
    const res = await request<WorkCalendarDay[]>({ url: "/work-calendar", params: { from, to } });
    setDays(res);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit() {
    const values = await form.validateFields();
    await request({
      method: "PUT",
      url: "/work-calendar",
      data: { date: values.date.format("YYYY-MM-DD"), classification: values.classification, note: values.note ?? "" },
    });
    form.resetFields();
    load();
  }

  // Removing an override returns the date to its default: a holiday on a Friday, a
  // working day otherwise.
  async function remove(day: WorkCalendarDay) {
    await request({ method: "DELETE", url: `/work-calendar/${day.id}` });
    load();
  }

  return (
    <div>
      <PageHeader title="Work Calendar" subtitle="GI annual work calendar — overrides to the default (all Fridays are public holidays)" />
      {canWrite && (
        <Card size="small" title="Add or change an override" style={{ marginBottom: 16 }}>
          <Form form={form} layout="inline" onFinish={submit}>
            <Form.Item name="date" rules={[{ required: true }]}>
              <DatePicker placeholder="Date" />
            </Form.Item>
            <Form.Item name="classification" rules={[{ required: true }]} initialValue="PUBLIC_HOLIDAY">
              <Select style={{ width: 200 }} options={CLASSIFICATIONS} />
            </Form.Item>
            <Form.Item name="note">
              <Input placeholder="Note" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}
      <Card size="small" title="Overrides (next 180 days)">
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          pagination={false}
          dataSource={days}
          columns={[
            { title: "Date", dataIndex: "date", render: (v) => dayjs(v).format(DT_FORMATS.date) },
            {
              title: "Classification",
              dataIndex: "classification",
              render: (v) => <Tag color={STATUS_COLORS[v] ?? "default"}>{String(v).replaceAll("_", " ")}</Tag>,
            },
            { title: "Note", dataIndex: "note" },
            ...(canWrite
              ? [
                  {
                    title: "",
                    key: "remove",
                    render: (_: unknown, r: WorkCalendarDay) => (
                      <Popconfirm
                        title="Remove this override?"
                        description={`${dayjs(r.date).format(DT_FORMATS.date)} goes back to ${dayjs(r.date).day() === 5 ? "a Friday holiday" : "a working day"}.`}
                        okText="Remove"
                        onConfirm={() => remove(r)}
                      >
                        <Button type="link" danger data-tour="work-calendar-remove">
                          Remove
                        </Button>
                      </Popconfirm>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>
    </div>
  );
}
